import { useParamsStore } from '@/stores/paramsStore.js';
import { useViewStore } from '@/stores/viewStore.js';
import { Button, Slider, Select, TextField } from '@/components/ui/index.js';
import { RESOLUTION_OPTIONS, parseResolution } from './resolution.js';
import { PresetsPanel } from '@/features/presets/PresetsPanel.js';

const ALGOS = [
  { value: 'simplex', label: 'Simplex' },
  { value: 'perlin', label: 'Perlin' },
  { value: 'worley', label: 'Worley (cellular)' },
  { value: 'value', label: 'Value' },
] as const;

const MODS = [
  { value: 'none', label: 'None' },
  { value: 'ridged', label: 'Ridged' },
  { value: 'terrace', label: 'Terrace' },
  { value: 'domain-warp', label: 'Domain warp' },
] as const;

const RENDER_MODES = [
  { value: '2d', label: '2D' },
  { value: '3d-smooth', label: '3D smooth' },
  { value: '3d-voxel', label: '3D voxel' },
] as const;

const ANIMS = [
  { value: 'none', label: 'None' },
  { value: 'rise', label: 'Rise' },
  { value: 'ripple', label: 'Ripple' },
  { value: 'dissolve', label: 'Dissolve' },
] as const;

export function ParamsPanel() {
  const { params, set, randomizeSeed, reset: resetParams } = useParamsStore();
  const { renderMode, setRenderMode, animationMode, setAnimation, reset: resetView } = useViewStore();

  const resetAll = () => { resetParams(); resetView(); };

  return (
    <aside className="w-80 shrink-0 border-r border-neutral-800 bg-neutral-900/50 overflow-y-auto p-4 space-y-5">
      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-neutral-400">Seed & algorithm</h2>
        <div className="flex gap-2">
          <TextField
            label="Seed"
            hint="Any string or integer. Same seed always produces the same world."
            value={String(params.seed)}
            onValueChange={(v) => set('seed', /^-?\d+$/.test(v) ? Number(v) : v)}
            className="flex-1"
          />
          <div className="flex items-end">
            <Button onClick={randomizeSeed} aria-label="Randomize seed" title="Randomize seed">🎲</Button>
          </div>
        </div>
        <Select label="Algorithm"
                hint="Base noise function. Each has a distinct visual character (Simplex is smooth, Worley is cellular)."
                value={params.algorithm}
                options={ALGOS} onValueChange={(v) => set('algorithm', v)} />
        <Select label="Resolution"
                hint="Heightmap grid size. Higher is sharper but slower to generate and paint."
                value={`${params.resolution}`}
                options={RESOLUTION_OPTIONS}
                onValueChange={(v) => set('resolution', parseResolution(v))} />
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-neutral-400">fBm</h2>
        <Slider label="Scale"
                hint="Larger = broader features (continents). Smaller = finer detail."
                min={1} max={500} step={1}
                value={params.scale} onValueChange={(v) => set('scale', v)} />
        <Slider label="Octaves"
                hint="Noise layers summed together. More = richer detail and more compute."
                min={1} max={8} step={1}
                value={params.octaves} onValueChange={(v) => set('octaves', v)} />
        <Slider label="Persistence"
                hint="How fast amplitude decays per octave. Higher = rougher, more chaotic terrain."
                min={0} max={1} step={0.01}
                value={params.persistence} onValueChange={(v) => set('persistence', v)}
                displayValue={(v) => v.toFixed(2)} />
        <Slider label="Lacunarity"
                hint="How fast frequency grows per octave. 2.0 is classic; higher = more angular detail."
                min={1} max={4} step={0.05}
                value={params.lacunarity} onValueChange={(v) => set('lacunarity', v)}
                displayValue={(v) => v.toFixed(2)} />
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-neutral-400">Modifier</h2>
        <Select label="Modifier"
                hint="Post-process applied to the base noise. None = raw fBm."
                value={params.modifier} options={MODS}
                onValueChange={(v) => set('modifier', v)} />
        {params.modifier === 'domain-warp' && (
          <Slider label="Warp strength"
                  hint="How far each sample point is displaced before sampling. Larger = more swirls and distortion."
                  min={0} max={100} step={1}
                  value={params.warpStrength} onValueChange={(v) => set('warpStrength', v)} />
        )}
        {params.modifier === 'terrace' && (
          <Slider label="Terrace steps"
                  hint="Discrete elevation plateaus. Lower = fewer, more dramatic cliffs."
                  min={2} max={32} step={1}
                  value={params.terraceSteps} onValueChange={(v) => set('terraceSteps', v)} />
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-neutral-400">World</h2>
        <Slider label="Water level"
                hint="Heights below this threshold are ocean in biome mode."
                min={0} max={1} step={0.01}
                value={params.waterLevel} onValueChange={(v) => set('waterLevel', v)}
                displayValue={(v) => v.toFixed(2)} />
        <Slider label="Elevation scale (3D)"
                hint="Vertical relief multiplier for the 3D viewer (Phase 7). 0 = flat, 1 = maximum."
                min={0} max={1} step={0.01}
                value={params.elevationScale} onValueChange={(v) => set('elevationScale', v)}
                displayValue={(v) => v.toFixed(2)} />
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-neutral-400">Rendering</h2>
        <Select label="Render mode"
                hint="2D paints the heightmap on a canvas; 3D variants wired in Phase 7."
                value={renderMode} options={RENDER_MODES}
                onValueChange={setRenderMode} />
        <Select label="Animation"
                hint="Optional reveal animation when a new heightmap arrives. Respects prefers-reduced-motion."
                value={animationMode} options={ANIMS}
                onValueChange={setAnimation} />
      </section>

      <Button variant="ghost" onClick={resetAll} className="w-full">Reset all</Button>
      <PresetsPanel />
    </aside>
  );
}
